import {
  TextureLoader,
  RepeatWrapping,
  MeshStandardMaterial,
  DoubleSide,
  VideoTexture,
  LinearFilter,
  RGBAFormat,
  MeshBasicMaterial,
} from "three";
import { useEffect, useRef, useState } from "react";
import hardWoodDiffuse from "../../assets/hardwood2_diffuse.jpg";
import hardWoodBump from "../../assets/hardwood2_bump.jpg";
import hardWoodRoughnss from "../../assets/hardwood2_roughness.jpg";
import tile1 from "../../assets/tile1.jpg";
import door1 from "../../assets/door1.jpg";
import door2 from "../../assets/door2.jpg";
import door3 from "../../assets/door3.jpg";
import logo from "../../assets/logo.jpg";
import sampleVideo from "../../assets/videos/sample.mp4";
import { loadTexture } from "../../utils/loadingManager";

function TexturesManager({ selectedApartmentNode, scene }) {
  const materialRef = useRef();
  const paintingMaterialRef = useRef();
  const doorMaterialRefs = useRef([]);
  const videoMaterialRef = useRef();
  const texturesCreatedRef = useRef(false); // Flag to track if textures have been created
  const isCreatingTextures = useRef(false); // Flag to track if textures have been created

  const [texturesLoaded, setTexturesLoaded] = useState(false);

  const doorImages = [door1, door2, door3];

  useEffect(() => {
    if (!texturesLoaded && !isCreatingTextures.current) {
      loadTextures();
    }
  }, []);

  useEffect(() => {
    if (texturesLoaded && selectedApartmentNode) {
      // console.log("Updating planes with new material:", selectedApartmentNode);
      // console.log("doorMaterialRefs", doorMaterialRefs.current);
      const doorFrames = selectedApartmentNode.children.filter((child) =>
        child.name.includes("doorframe")
      );
      doorFrames.forEach((door) => {
        door.children.forEach((door) => {
          const doorPlane = door.children.find((child) =>
            child.name.includes("plane")
          );
          if (doorPlane) {
            const randomIndex = Math.floor(
              Math.random() * doorMaterialRefs.current.length
            );
            // console.log("randomIndex", randomIndex);
            doorPlane.material = doorMaterialRefs.current[randomIndex];
            doorPlane.material.needsUpdate = true;
          }
        });
        door.material.color.setHex(0x000000);
      });
      const tvFrameMesh = selectedApartmentNode.children.find((child) =>
        child.name.includes("tv_frame")
      );
      if (tvFrameMesh) {
        const tvScreen = tvFrameMesh.children.find((child) =>
          child.name.includes("plane")
        );
        if (tvScreen) {
          tvScreen.material = videoMaterialRef.current;
          tvScreen.material.needsUpdate = true;
        }
      }
      const livingRoomMesh = selectedApartmentNode.children.find((child) =>
        child.name.includes("liveroom")
      );
      if (livingRoomMesh) {
        livingRoomMesh.children.forEach((child) => {
          child.visible = false;
        });
        livingRoomMesh.material = materialRef.current;
      }
      const kitchenMesh = selectedApartmentNode.children.find((child) =>
        child.name.includes("kitchen")
      );
      if (kitchenMesh) {
        kitchenMesh.children.forEach((child) => {
          child.visible = false;
        });

        kitchenMesh.material = materialRef.current;
      }
      const balconyMesh = selectedApartmentNode.children.find((child) =>
        child.name.includes("balcony")
      );
      if (balconyMesh) {
        balconyMesh.children.forEach((child) => {
          child.visible = false;
        });

        balconyMesh.material = materialRef.current;
      }


      const paintingFrameMesh = selectedApartmentNode.children.find((child) =>
        child.name.includes("painting_frame")
      );
      if (paintingFrameMesh) {
        const paintingMesh = paintingFrameMesh.children.find((child) =>
          child.name.includes("painting")
        );

        paintingMesh.material = paintingMaterialRef.current;
        // paintingMesh.material = materialRef.current;
      }

    }
  }, [selectedApartmentNode]);

  const createVideoTexture = async () => {
    const video = document.createElement("video");
    video.src = sampleVideo;
    video.crossOrigin = "anonymous";
    video.loop = true;
    video.muted = true;

    // Add event listener to play the video when it's ready
    video.addEventListener("canplay", () => {
      video.play().catch((error) => {
        console.error("Error playing video:", error);
      });
    });

    // Create a texture from the video
    const texture = new VideoTexture(video);
    texture.minFilter = LinearFilter;
    texture.magFilter = LinearFilter;
    texture.format = RGBAFormat;
    const material = new MeshBasicMaterial({ map: texture });
    return material;
  };

  const createDoorsTexture = async () => {
    // console.log("Creating doors texture...", doorMaterialRefs.current.length, texturesLoaded);
    if (doorMaterialRefs.current.length !== 0) return;

    const promises = doorImages.map(async (image, index) => {
      const texture = await loadTexture(image);
      const material = new MeshBasicMaterial({ map: texture });
      // console.log("adding doorMaterial", index);
      doorMaterialRefs.current.push(material);
    });

    await Promise.all(promises);
    texturesCreatedRef.current = true; // Set the flag to true after creating textures
  };

  const loadTextures = async () => {
    isCreatingTextures.current = true
    // console.log("Loading textures...");

    // materialRef.current = null;
    // videoMaterialRef.current = null;

    const logoTexture = await loadTexture(logo);
    const imgTexture = await loadTexture(tile1);
    const bumpTexture = await loadTexture(hardWoodBump);
    const roughnessTexture = await loadTexture(hardWoodRoughnss);
    await createDoorsTexture();

    imgTexture.wrapS = RepeatWrapping;
    imgTexture.wrapT = RepeatWrapping;
    imgTexture.repeat.set(10, 10); // Adjust the repeat values as needed
    materialRef.current = new MeshBasicMaterial({
      map: imgTexture,
      transparent: false,
      side: DoubleSide, // Make the texture double-sided
    });
    // materialRef.current = new MeshStandardMaterial({
    //   map: imgTexture,
    //   transparent: false,
    //   depthTest: true,
    //   side: DoubleSide, // Make the texture double-sided
    //   bumpMap: bumpTexture,
    //   roughnessMap: roughnessTexture,
    // });

    paintingMaterialRef.current = new MeshBasicMaterial({ map: logoTexture });

    // console.log("Material created:", materialRef.current);
    // imgTexture.repeat.set(20, 20); // Adjust the repeat values as needed

    // materialRef.current = new MeshStandardMaterial({
    //   map: imgTexture,
    //   transparent: false,
    //   depthTest: true,
    // });
    videoMaterialRef.current = await createVideoTexture();

    setTexturesLoaded(true);
  };

  return null;
}

export default TexturesManager;